import axios from 'axios';

const ALPACA_DATA_URL = import.meta.env.VITE_ALPACA_DATA_URL || 'https://data.sandbox.alpaca.markets';
const ALPACA_KEY = import.meta.env.VITE_ALPACA_API_KEY || '';
const ALPACA_SECRET = import.meta.env.VITE_ALPACA_API_SECRET || '';

/**
 * Comisión escalonada Alpaca (modelo financiero v14).
 * Espejo de backend/utils/pricing.js calculateAlpacaCommission.
 *
 * Tramos (TOTAL al cliente, IVA 19% incluido):
 *   < 12 USD       -> below_min, no se puede operar
 *   12 - 50        -> $0.50 flat
 *   50 - 500       -> $2.00 flat
 *   500+           -> 0.3% (con tope $30)
 *
 * Devuelve {feeTotal, feeBase, iva, tier, effectivePct, minTradeUsd}.
 * Cliente paga feeTotal. El desglose base/iva es para la factura electronica.
 */
export const IVA_RATE = 0.19;
export const ALPACA_MIN_TRADE_USD = 12;

export function calculateAlpacaCommission(amountUsd) {
  const minTrade = ALPACA_MIN_TRADE_USD;
  if (!Number.isFinite(amountUsd) || amountUsd < minTrade) {
    return { feeTotal: 0, feeBase: 0, iva: 0, tier: 'below_min', effectivePct: 0, minTradeUsd: minTrade };
  }

  let feeTotal, tier;
  if (amountUsd < 50)        { feeTotal = 0.50; tier = 'tier1'; }
  else if (amountUsd < 500)  { feeTotal = 2.00; tier = 'tier2'; }
  else {
    const pctFee = amountUsd * 0.003; // 0.3%
    if (pctFee > 30) { feeTotal = 30; tier = 'cap'; }
    else             { feeTotal = pctFee; tier = 'tier3'; }
  }

  const feeBase = feeTotal / (1 + IVA_RATE);
  const iva = feeTotal - feeBase;
  const effectivePct = amountUsd > 0 ? (feeTotal / amountUsd) * 100 : 0;
  return { feeTotal, feeBase, iva, tier, effectivePct, minTradeUsd: minTrade };
}

const alpaca = axios.create({
  baseURL: ALPACA_DATA_URL,
  headers: {
    'APCA-API-KEY-ID': ALPACA_KEY,
    'APCA-API-SECRET-KEY': ALPACA_SECRET,
  },
});

/**
 * Cliente para el backend de investments-api (rutas /api/orders/...).
 * Las ordenes ya NO se envian a Alpaca directo desde aqui: pasan por nuestro
 * backend, que cobra comision + IVA y emite factura electronica Siigo.
 */
const INVESTMENTS_API_URL = import.meta.env.VITE_INVESTMENTS_API_URL || 'http://localhost:5001';
const ordersBackend = axios.create({
  baseURL: INVESTMENTS_API_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});
ordersBackend.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const getSnapshot = async (symbol) => {
  const { data } = await alpaca.get(`/v2/stocks/${symbol}/snapshot`);
  return data;
};

export const getSnapshots = async (symbols) => {
  const { data } = await alpaca.get('/v2/stocks/snapshots', {
    params: { symbols: symbols.join(',') },
  });
  return data;
};

export const getBars = async (symbol, timeframe = '1Day', start, end, limit = 1000) => {
  const params = { timeframe, limit, adjustment: 'all' };
  if (start) params.start = start;
  if (end) params.end = end;
  const { data } = await alpaca.get(`/v2/stocks/${symbol}/bars`, { params });
  return data.bars || [];
};

export const getNews = async (symbols, limit = 10) => {
  const { data } = await alpaca.get('/v1beta1/news', {
    params: { symbols: symbols.join(','), limit, sort: 'desc' },
  });
  return data.news || [];
};

export const getOptionContracts = async (underlying, type = 'call', expirationDate) => {
  const params = { underlying_symbols: underlying, type, status: 'active', limit: 1000 };
  if (expirationDate) {
    params.expiration_date = expirationDate;
  } else {
    const today = new Date().toISOString().split('T')[0];
    params.expiration_date_gte = today;
    params.expiration_date_lte = '2029-12-31';
  }
  const { data } = await broker.get('/v1/options/contracts', { params });
  return data.option_contracts || [];
};

export const getOptionSnapshots = async (underlying) => {
  const { data } = await alpaca.get(`/v1beta1/options/snapshots/${underlying}`);
  return data.snapshots || {};
};

export const getMostActives = async (top = 10) => {
  const { data } = await alpaca.get('/v1beta1/screener/stocks/most-actives', {
    params: { top },
  });
  return data.most_actives || [];
};

export const getMarketMovers = async (top = 10) => {
  const { data } = await alpaca.get('/v1beta1/screener/stocks/movers', {
    params: { top },
  });
  return data;
};

const BROKER_URL = 'https://broker-api.sandbox.alpaca.markets';
const BROKER_KEY = import.meta.env.VITE_ALPACA_BROKER_KEY || ALPACA_KEY;
const BROKER_SECRET = import.meta.env.VITE_ALPACA_BROKER_SECRET || ALPACA_SECRET;

const broker = axios.create({
  baseURL: BROKER_URL,
  auth: { username: BROKER_KEY, password: BROKER_SECRET },
});

// Default trading account ID (sandbox)
const DEFAULT_ACCOUNT_ID = import.meta.env.VITE_ALPACA_ACCOUNT_ID || 'ea26b2d8-8385-4177-89cc-a2a986561ab7';

// Map FlowexPay users to Alpaca sandbox accounts
const ALPACA_ACCOUNTS = {
  'default': DEFAULT_ACCOUNT_ID,
  'ivan@flowexpay.com': 'ea26b2d8-8385-4177-89cc-a2a986561ab7',
  'jasarmientosa@hotmail.com': 'af3773df-dd7a-4c8d-b067-26ab3b7309bf',
  // Demo accounts created for Progresión meeting (May 2026)
  'juanlamboglia@icloud.com': '766d65e8-ca89-4fa7-a957-0eb5dce4e828',
  'ivansarmi@hotmail.com': 'e3c8246e-fe46-4825-a1e3-dc3e87194212',
  'dr.ioanisvalencia@gmail.com': '4fa2b4aa-c692-4125-b174-4f9016bc4da7',
};

export const getAlpacaAccountId = (email) => {
  // 1. Prefer the user's own account ID stored after onboarding (real users)
  try {
    const profileStr = localStorage.getItem('fp_investor_profile');
    if (profileStr) {
      const profile = JSON.parse(profileStr);
      if (profile?.alpacaAccountId) return profile.alpacaAccountId;
    }
  } catch {}

  // 2. Fallback to legacy demo mapping (Juan, Iván, Ioanis, etc.)
  if (!email) return DEFAULT_ACCOUNT_ID;
  const lower = email.toLowerCase();
  return ALPACA_ACCOUNTS[lower] || DEFAULT_ACCOUNT_ID;
};

// --- Trading Functions ---

export const getAccount = async (accountId) => {
  const id = accountId || DEFAULT_ACCOUNT_ID;
  const { data } = await broker.get(`/v1/trading/accounts/${id}/account`);
  return data;
};

export const getPositions = async (accountId) => {
  const id = accountId || DEFAULT_ACCOUNT_ID;
  const { data } = await broker.get(`/v1/trading/accounts/${id}/positions`);
  return data;
};

export const getOrders = async (status = 'all', limit = 20, accountId) => {
  const id = accountId || DEFAULT_ACCOUNT_ID;
  const { data } = await broker.get(`/v1/trading/accounts/${id}/orders`, {
    params: { status, limit },
  });
  return data;
};

/**
 * Ejecuta una orden de acciones via backend FlowexPay (NO directo a Alpaca).
 *
 * El backend:
 *  1. Valida usuario + monto minimo $12 USD
 *  2. Calcula comision escalonada + IVA
 *  3. Debita `notional + feeTotal` del vault del cliente (TODO Fireblocks)
 *  4. Ejecuta el trade en Alpaca
 *  5. Guarda Trade en MongoDB para audit + factura Siigo
 *
 * Retorna {success, trade: {feeBase, iva, feeTotal, ...}, id, status, ...}
 * Compatible con el shape anterior (id, status, symbol, side, notional)
 * mas el nuevo `trade` con desglose de comision.
 */
export const placeStockOrder = async ({ symbol, notional, qty, side = 'buy', type = 'market', timeInForce = 'day' }) => {
  const body = { symbol, side, type, timeInForce };
  if (notional) body.notional = parseFloat(notional);
  else if (qty) body.qty = parseFloat(qty);
  const { data } = await ordersBackend.post('/api/orders/stock', body);
  return data;
};

export const placeOptionOrder = async ({ symbol, qty, side = 'buy', type = 'market', timeInForce = 'day', accountId }) => {
  const id = accountId || DEFAULT_ACCOUNT_ID;
  const order = {
    symbol,
    qty: String(qty),
    side,
    type,
    time_in_force: timeInForce,
  };
  const { data } = await broker.post(`/v1/trading/accounts/${id}/orders`, order);
  return data;
};

export const searchAssets = async (query) => {
  try {
    const upper = query.toUpperCase();
    // Try exact symbol match first via snapshot
    const { data } = await alpaca.get('/v2/stocks/snapshots', {
      params: { symbols: upper },
    });
    if (data && Object.keys(data).length > 0) {
      return Object.keys(data).map((s) => ({ symbol: s, name: s }));
    }
    return [];
  } catch {
    return [];
  }
};

export const periodToTimeframe = (period) => {
  switch (period) {
    case '1D': return { timeframe: '5Min', days: 1 };
    case '1S': return { timeframe: '30Min', days: 7 };
    case '1M': return { timeframe: '1Day', days: 30 };
    case '3M': return { timeframe: '1Day', days: 90 };
    case '1A': return { timeframe: '1Week', days: 365 };
    case 'Max': return { timeframe: '1Month', days: 365 * 5 };
    default: return { timeframe: '1Day', days: 30 };
  }
};

export default alpaca;
